export type TLang = "en" | "ru" | "de" | "ua";
export type TLangAndNone = TLang | "none";

export const TLangEnum: Record<TLang, TLang> = {
  de: "de",
  ru: "ru",
  en: "en",
  ua: "ua",
};
export const ALL_LANGS = [...Object.values(TLangEnum)] as TLang[];
export const isSupportedLang = (langLike: string): langLike is TLang =>
  ALL_LANGS.includes(langLike as TLang);

export type TExercise = "wordCards" | "wordCardsReverse";
export const TExerciseEnum = {
  wordCards: "wordCards",
  wordCardsReverse: "wordCardsReverse",
};
export const ALL_EXERCISES = [...Object.values(TExerciseEnum)] as TExercise[];

export type TDeckTemplate = "default" | "deVerb";
export const TDeckTemplateEnum = {
  default: "default",
  deVerb: "deVerb",
};
export const ALL_DECK_TEMPLATES = [
  ...Object.values(TDeckTemplateEnum),
] as TDeckTemplate[];

export type TExerciseStatus = {
  lastProbeAt: Date;
  nextProbeAt: Date;
  tryouts: number;
  reseted: number;
};

export type TWord = {
  uid: string;
  deckUid: string;
  // front
  definitions: {
    [key in TLangAndNone]?: string;
  };
  note: string | null;
  // back
  value: string;
  valueNote: string | null;
  valueNormal: string | null;
  transcription: string | null;
  image: string | null;
  // other
  createdAt: Date;
  changedAt: Date;
  status: { [K in TExercise]?: TExerciseStatus };
  tags: string[];
  // resources
  valueResourceUid?: string | null;
  valueResourceBinary?: TBinaryUpdate | null;
  definitionResourceUid?: string | null;
  definitionResourceBinary?: TBinaryUpdate | null;
};

export type TWordUpdate = Omit<
  TWord,
  | "transcription"
  | "uid"
  | "deckUid"
  | "createdAt"
  | "changedAt"
  | "status"
  | "valueNormal"
>;

export const ALL_WORD_SORTS = [
  "newest",
  "oldest",
  "pending-first",
  "pending-last",
] as const;

export type TWordSort = (typeof ALL_WORD_SORTS)[number];

export type TWordSearchFilters = {
  text: string;
  sort: TWordSort;
};

export type TDeck = {
  uid: string;
  name: string;
  lang: TLang | null;
  definitionLangs: TLangAndNone[];
  createdAt: Date;
  templateType: TDeckTemplate;
  exercises: TExercise[];
};

export type TDeckUpdate = Pick<
  TDeck,
  "name" | "lang" | "templateType" | "exercises" | "definitionLangs"
> & {
  uid?: string;
};

export type TDeckRich = TDeck & {
  wordsCount: number;
  pendingWordsCount: number;
  status: Partial<Record<TExercise, TExerciseInfo>>;
};

export type TExerciseInfo = {
  count: number;
  closest: Date | null;
};

export type TBackup = {
  cards: TWord[];
  decks: TDeck[];
  createdAt: Date;
};

export type TTestUpdate = { name: string };

export type TTest = {
  uid: string;
  name: string;
  createdAt: Date;
};

export type TTestRich = TTest & {
  questionsCount: number;
  pendingQuestionsCount: number;
  status: {
    count: number;
    closest: Date | null;
  };
};

export type TQuestionUpdate = Pick<
  TQuestion,
  "question" | "hint" | "options" | "resourceUid" | "resourceBinary"
>;

export type TQuestion = {
  uid: string;
  testUid: string;
  createdAt: Date;
  hint: string | null;
  question: string;
  options: string[];
  lastProbeAt: Date;
  nextProbeAt: Date;
  reseted: number;
  tryouts: number;
  resourceUid?: string | null;
  resourceBinary?: TBinaryUpdate | null;
};

export type TBinaryUpdate = { action: "remove" | "set"; data: string };

export type TUser = {
  uid: string;
  login: string;
  createdAt: Date;
  isAdmin: boolean;
  email: string | null;
  // Only to update password.
  oldPassword?: string;
  newPassword?: string;
};

export type TUserUpdate = Omit<TUser, "uid" | "createdAt">;

export type TRichUser = TUser & {
  words: { total: number; pending: number };
  decks: number;
  questions: { total: number; pending: number };
  tests: number;
};

export type TDashboard = {
  decks: TDeckRich[];
  tests: TTestRich[];
  pending: number;
  total: number;
};